// OwnersPage — /owners: the owner & property branch (Agreement Command Center).
//
// Two tabs (URL param `tab`): Engagements — every owner-agreement thread on
// the same phase vocabulary as the rest of the registry; Properties — a modest
// reference registry of managed properties with doorways to the branch's other
// reference surfaces (HOA registry, PM directory).
//
// Each engagement row has a Details expander (owner-sync spec §6/§7): a
// "Request a W-9" link per owner, and the engagement's AppFolio sync steps
// (GET /api/owner-sync/:engagementGroupId) with Retry / Mark resolved / "Not in
// AppFolio — send again" (each only where the step's flag allows it). It loads
// on expand — one engagement at a time, never a fan-out across every row — and
// the sync part hides itself when the endpoint is absent / off or has no steps.
// Marking the owner-CREATE step resolved asks which AppFolio owner it made (an
// owner search picker; the server refuses a create resolve without one), so the
// steps waiting on that owner can run. The signed City utilities agreement's
// attachment waits for its AppFolio PROPERTY; that step offers "Link the AppFolio
// property" (a property picker, checked against the onboarding's address).
//
// The Details expander also says whether the property is inside the City of Walla
// Walla limits (docs/city-utility-agreement-plan.md §3) — decided from county
// records when the intake went out, with a Set control for staff to decide it
// (PATCH /api/owner-intake/:groupId/jurisdiction) when the county couldn't tell.

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchOwners,
  formatAge,
  formatWhen,
  type OwnersPayload,
} from '@/lib/commandCenterClient';
import {
  getOwnerIntakeDetail,
  resendOwnerIntake,
  setOwnerIntakeJurisdiction,
  type OwnerIntakeDetailOwner,
} from '@/lib/ownerIntakeClient';
import {
  JURISDICTION_CHOICES,
  jurisdictionLine,
  type Jurisdiction,
  type OwnerIntakeJurisdiction,
} from '@/lib/jurisdiction';
import { ApiError } from '@/lib/api';
import {
  isOwnerCreateOp,
  notInAppFolioAction,
  propertyResultRow,
  syncActionErrorMessage,
  syncOpsToShow,
  w9RequestHref,
  type OwnerMatch,
  type OwnerSyncOpView,
} from '@/lib/ownerSync';
import {
  getOwnerSync,
  resendOwnerSyncOpConfirmed,
  resolveOwnerSyncOp,
  retryOwnerSyncOp,
  type OwnerSyncView,
} from '@/lib/ownerSyncClient';
import { toast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { OwnerSearchPicker } from '@/components/ownerSync/OwnerSearchPicker';
import { OwnerSyncOpsTable } from '@/components/ownerSync/OwnerSyncOpsTable';
import { LinkPropertyDialog } from '@/components/ownerSync/LinkPropertyDialog';
import { OcEmpty, OcError, OcLoading, PhaseChip } from '@/components/oc/bits';

/** Plain-language message from a failed mutation (mirrors ThreadDetailPage). */
function mutationError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `${fallback} (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : fallback;
}

/**
 * A refused Retry / Mark resolved, in words: the server's plain message, never a
 * machine code (409 owner_required) and never anything carrying an id (an op
 * key names the engagement) — see syncActionErrorMessage.
 */
function syncActionError(err: unknown, fallback: string): string {
  return err instanceof ApiError ? syncActionErrorMessage(err.body, fallback) : fallback;
}

/**
 * "Inside the City of Walla Walla limits?" for one engagement, with a Set control
 * (a small select + Save). The City utilities agreement goes out only for a decided
 * "inside"; when the county couldn't tell, staff decide here.
 */
function JurisdictionPanel({
  caseId,
  value,
  onSaved,
}: {
  caseId: string;
  value: OwnerIntakeJurisdiction | null;
  onSaved: (next: OwnerIntakeJurisdiction | null) => void;
}): React.JSX.Element {
  const line = jurisdictionLine(value);
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState<Jurisdiction>(value?.jurisdiction ?? 'city_of_walla_walla');
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    setChoice(
      value && value.jurisdiction !== 'unknown' ? value.jurisdiction : 'city_of_walla_walla',
    );
    setEditing(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const next = await setOwnerIntakeJurisdiction(caseId, choice);
      onSaved(next);
      setEditing(false);
      toast.success('Saved.');
    } catch (err) {
      toast.error(mutationError(err, "Couldn't save that"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="oc-panel">
      <span className="oc-kicker">City of Walla Walla</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 auto' }}>
          {line.headline}
          {line.detail ? <span className="oc-meta"> · {line.detail}</span> : null}
        </span>
        {!editing && (
          <button
            type="button"
            className="btn"
            data-variant={line.needsSetting ? 'primary' : 'secondary'}
            data-size="sm"
            onClick={startEditing}
          >
            {line.needsSetting ? 'Set' : 'Change'}
          </button>
        )}
      </div>
      {editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <label htmlFor={`jurisdiction-${caseId}`} className="oc-meta">
            The property is
          </label>
          <select
            id={`jurisdiction-${caseId}`}
            className="input"
            style={{ width: 'auto' }}
            value={choice}
            disabled={saving}
            onChange={(e) => setChoice(e.target.value as Jurisdiction)}
          >
            {JURISDICTION_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn"
            data-variant="primary"
            data-size="sm"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            disabled={saving}
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The expanded part of one engagement row: whether the property is inside the City
 * of Walla Walla, who can be asked for a W-9, and the AppFolio sync steps. Mounted
 * only while the row is open.
 */
function EngagementDetails({
  caseId,
  propertyAddress,
}: {
  caseId: string;
  /** The onboarding's property address — seeds "Link the AppFolio property". */
  propertyAddress: string | null;
}): React.JSX.Element {
  const [owners, setOwners] = useState<OwnerIntakeDetailOwner[] | null>(null);
  const [ownersFailed, setOwnersFailed] = useState(false);
  /** undefined = still loading / no intake record (the panel hides); null = never checked. */
  const [jurisdiction, setJurisdiction] = useState<OwnerIntakeJurisdiction | null | undefined>(
    undefined,
  );
  const [sync, setSync] = useState<OwnerSyncView | null>(null);
  const [syncLoading, setSyncLoading] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [busyOpKey, setBusyOpKey] = useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = useState<OwnerSyncOpView | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveError, setResolveError] = useState<string | null>(null);
  /** A CREATE step is resolved WITH the AppFolio owner it made (server: 409 owner_required otherwise). */
  const [resolveOwner, setResolveOwner] = useState<OwnerMatch | null>(null);
  const resolveNeedsOwner = resolveTarget !== null && isOwnerCreateOp(resolveTarget);
  const resolveIfMissing = resolveTarget ? notInAppFolioAction(resolveTarget) : null;
  // "Not in AppFolio — send again": a fresh attempt for a stuck step whose outcome
  // is unknown, only after staff confirm they checked AppFolio (a duplicate otherwise).
  const [resendTarget, setResendTarget] = useState<OwnerSyncOpView | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  // "Link the AppFolio property": the waiting City utilities agreement attachment.
  const [linkTarget, setLinkTarget] = useState<OwnerSyncOpView | null>(null);

  const loadSync = useCallback(async () => {
    try {
      // null = endpoint absent (404) or switched off (503) → the section hides.
      setSync(await getOwnerSync(caseId));
      setSyncError(null);
    } catch {
      setSyncError("Couldn't load the AppFolio sync status.");
    } finally {
      setSyncLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await getOwnerIntakeDetail(caseId);
        if (!cancelled) {
          setOwners(d.owners);
          setJurisdiction(d.jurisdiction ?? null);
        }
      } catch {
        if (!cancelled) setOwnersFailed(true);
      }
    })();
    void loadSync();
    return () => {
      cancelled = true;
    };
  }, [caseId, loadSync]);

  const handleRetry = async (op: OwnerSyncOpView) => {
    if (busyOpKey) return;
    setBusyOpKey(op.opKey);
    try {
      await retryOwnerSyncOp(op.opKey);
      toast.success(`Retrying: ${op.label}.`);
      await loadSync();
    } catch (err) {
      toast.error(syncActionError(err, "Couldn't retry that step."));
    } finally {
      setBusyOpKey(null);
    }
  };

  const openResendConfirmed = (op: OwnerSyncOpView) => {
    setResendTarget(op);
    setResendError(null);
  };

  const handleResendConfirmed = async () => {
    if (!resendTarget || busyOpKey) return;
    setBusyOpKey(resendTarget.opKey);
    setResendError(null);
    try {
      await resendOwnerSyncOpConfirmed(resendTarget.opKey);
      toast.success(`Sending again: ${resendTarget.label}.`);
      setResendTarget(null);
      await loadSync();
    } catch (err) {
      setResendError(syncActionError(err, "Couldn't send that step again."));
    } finally {
      setBusyOpKey(null);
    }
  };

  const openResolve = (op: OwnerSyncOpView) => {
    setResolveTarget(op);
    setResolveNote('');
    setResolveError(null);
    setResolveOwner(null);
  };

  const handleResolve = async () => {
    if (!resolveTarget || busyOpKey) return;
    if (resolveNeedsOwner && !resolveOwner) {
      setResolveError('Choose the owner in AppFolio before marking this step done.');
      return;
    }
    const owner = resolveNeedsOwner ? resolveOwner : null;
    setBusyOpKey(resolveTarget.opKey);
    setResolveError(null);
    try {
      await resolveOwnerSyncOp(resolveTarget.opKey, resolveNote, owner?.appfolioOwnerId);
      toast.success(
        owner
          ? `Marked done: ${resolveTarget.label}, with ${owner.displayName}.`
          : `Marked done: ${resolveTarget.label}.`,
      );
      setResolveTarget(null);
      await loadSync();
    } catch (err) {
      setResolveError(syncActionError(err, "Couldn't mark that step resolved."));
    } finally {
      setBusyOpKey(null);
    }
  };

  const ops = syncOpsToShow(sync);

  return (
    <div className="stack-sm" style={{ padding: 'var(--space-2) 0' }}>
      {jurisdiction !== undefined && (
        <JurisdictionPanel caseId={caseId} value={jurisdiction} onSaved={setJurisdiction} />
      )}
      <div className="oc-panel">
        <span className="oc-kicker">W-9</span>
        {owners && owners.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {owners.map((o) => (
              <li key={o.role} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 auto' }}>
                  {o.name}
                  {o.email ? <span className="oc-meta"> · {o.email}</span> : null}
                </span>
                <Link className="btn" data-variant="secondary" data-size="sm" to={w9RequestHref(o)}>
                  Request a W-9
                </Link>
              </li>
            ))}
          </ul>
        ) : ownersFailed ? (
          <Link className="btn" data-variant="secondary" data-size="sm" to={w9RequestHref({})}>
            Request a W-9
          </Link>
        ) : (
          <span className="oc-meta">Loading the owners…</span>
        )}
      </div>

      {syncLoading ? null : syncError ? (
        <p className="oc-meta" style={{ margin: 0 }}>{syncError}</p>
      ) : ops.length > 0 ? (
        <div className="oc-panel">
          <span className="oc-kicker">AppFolio sync</span>
          <OwnerSyncOpsTable
            ops={ops}
            busyOpKey={busyOpKey}
            onRetry={(op) => void handleRetry(op)}
            onResolve={openResolve}
            onResendConfirmed={openResendConfirmed}
            onLinkProperty={setLinkTarget}
            formatWhen={formatWhen}
          />
        </div>
      ) : null}

      <ConfirmDialog
        open={resolveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResolveTarget(null);
        }}
        title="Mark this step as done?"
        description={
          !resolveTarget
            ? ''
            : resolveNeedsOwner
              ? `“${resolveTarget.label}”${resolveTarget.ownerName ? ` for ${resolveTarget.ownerName}` : ''}: check AppFolio. If the owner is there, pick them below — the contact note, attachments and taxpayer ID waiting on this step then go onto that owner.${resolveIfMissing ? ` If the owner is not in AppFolio, cancel and ${resolveIfMissing}.` : ''}`
              : `“${resolveTarget.label}”${resolveTarget.ownerName ? ` for ${resolveTarget.ownerName}` : ''}: use this when the change is already in AppFolio — for example, you made it by hand. We stop retrying it, and anything waiting on it goes ahead.`
        }
        confirmLabel="Mark resolved"
        tone="primary"
        busy={busyOpKey !== null && busyOpKey === resolveTarget?.opKey}
        busyLabel="Saving…"
        error={resolveError}
        confirmDisabled={resolveNeedsOwner && !resolveOwner}
        onConfirm={() => void handleResolve()}
      >
        {resolveNeedsOwner && resolveTarget && (
          <OwnerSearchPicker
            key={resolveTarget.opKey}
            id={`resolve-owner-${caseId}`}
            initialQuery={resolveTarget.ownerName ?? ''}
            selected={resolveOwner}
            ifMissing={resolveIfMissing}
            onPick={(m) => {
              setResolveOwner(m);
              setResolveError(null);
            }}
            disabled={busyOpKey !== null}
          />
        )}
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor={`resolve-note-${caseId}`}>Note (optional)</label>
          <textarea
            id={`resolve-note-${caseId}`}
            className="input"
            rows={2}
            placeholder="What you checked or changed in AppFolio"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
          />
        </div>
      </ConfirmDialog>

      <LinkPropertyDialog
        caseId={caseId}
        op={linkTarget}
        intakeAddress={propertyAddress}
        loadProperties={listProperties}
        onClose={() => setLinkTarget(null)}
        onLinked={(picked, released) => {
          setLinkTarget(null);
          const where = propertyResultRow(picked).detail || propertyResultRow(picked).name;
          toast.success(
            released > 0 ? `Linked ${where}. The signed City form is on its way to AppFolio.` : `Linked ${where}.`,
          );
          void loadSync();
        }}
      />

      <ConfirmDialog
        open={resendTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResendTarget(null);
        }}
        title="Send this step again?"
        description="Only do this after checking AppFolio. If the owner (or note, or document) is already there, sending again creates a duplicate. Pick it with Mark resolved instead."
        confirmLabel="It's not there — send again"
        tone="danger"
        busy={busyOpKey !== null && busyOpKey === resendTarget?.opKey}
        busyLabel="Sending…"
        error={resendError}
        onConfirm={() => void handleResendConfirmed()}
      >
        {resendTarget && (
          <p className="oc-meta" style={{ margin: 0 }}>
            Step: {resendTarget.label}
            {resendTarget.ownerName ? ` · ${resendTarget.ownerName}` : ''}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
// Same GET /api/properties shape + normalization the Renewal picker uses.
import { listProperties, type PropertyOption } from '@/hooks/useLeaseLifecycle';

type OwnersTab = 'engagements' | 'properties';

export function OwnersPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: OwnersTab = searchParams.get('tab') === 'properties' ? 'properties' : 'engagements';

  const setTab = (next: OwnersTab) => {
    if (next === tab) return;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === 'engagements') p.delete('tab');
      else p.set('tab', next);
      return p;
    });
  };

  // --- engagements ---
  const [owners, setOwners] = useState<OwnersPayload | null>(null);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [ownersError, setOwnersError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchOwners();
        if (!cancelled) setOwners(res);
      } catch (err) {
        if (!cancelled) {
          setOwnersError(err instanceof Error ? err.message : 'Failed to load owner engagements');
        }
      } finally {
        if (!cancelled) setOwnersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- properties (lazy: fetched the first time the tab is opened) ---
  const [properties, setProperties] = useState<PropertyOption[] | null>(null);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propsError, setPropsError] = useState<string | null>(null);
  const [propQuery, setPropQuery] = useState('');

  useEffect(() => {
    if (tab !== 'properties' || properties !== null || propsLoading) return;
    let cancelled = false;
    setPropsLoading(true);
    setPropsError(null);
    (async () => {
      try {
        const list = await listProperties();
        if (!cancelled) setProperties(list);
      } catch (err) {
        if (!cancelled) {
          setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
        }
      } finally {
        if (!cancelled) setPropsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, properties, propsLoading]);

  const filteredProperties = useMemo(() => {
    const q = propQuery.trim().toLowerCase();
    const list = properties ?? [];
    if (!q) return list;
    return list.filter(
      (p) => p.name.toLowerCase().includes(q) || (p.address ?? '').toLowerCase().includes(q),
    );
  }, [properties, propQuery]);

  const engagements = owners?.engagements ?? [];

  // Per-row resend state: which caseId is mid-send, and which have been sent this
  // session (so the button reads "Resent" and won't be spammed without a reload).
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resentIds, setResentIds] = useState<Set<string>>(new Set());
  // Which engagement's Details are open (one at a time — each open loads its own data).
  const [openCaseId, setOpenCaseId] = useState<string | null>(null);

  const handleResend = async (caseId: string) => {
    if (resendingId) return;
    setResendingId(caseId);
    try {
      const res = await resendOwnerIntake(caseId);
      setResentIds((prev) => new Set(prev).add(caseId));
      toast.success(
        `Reminder sent to ${res.ownersEmailed} owner${res.ownersEmailed === 1 ? '' : 's'}.`,
      );
    } catch (err) {
      toast.error(mutationError(err, "Couldn't resend the intake"));
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="oc-page">
      <div className="oc-page-header">
        <h1 className="oc-page-title">Owners &amp; properties</h1>
        {tab === 'engagements' && (
          <span className="oc-meta">
            as of {formatWhen(owners?.generatedAt ?? null)} · {engagements.length} rows
          </span>
        )}
      </div>

      <div className="oc-filterbar">
        <button
          type="button"
          className="oc-view-tab"
          data-active={tab === 'engagements' ? 'true' : undefined}
          onClick={() => setTab('engagements')}
        >
          Engagements
        </button>
        <button
          type="button"
          className="oc-view-tab"
          data-active={tab === 'properties' ? 'true' : undefined}
          onClick={() => setTab('properties')}
        >
          Properties
        </button>
        {tab === 'properties' && (
          <input
            className="input oc-search"
            placeholder="Filter by name, address…"
            aria-label="Filter properties"
            value={propQuery}
            onChange={(e) => setPropQuery(e.target.value)}
          />
        )}
      </div>

      {tab === 'engagements' && (
        <>
          {ownersLoading && <OcLoading />}
          {!ownersLoading && ownersError && <OcError>{ownersError}</OcError>}
          {!ownersLoading && !ownersError && engagements.length === 0 && (
            <OcEmpty>No owner engagements yet.</OcEmpty>
          )}
          {!ownersLoading && !ownersError && engagements.length > 0 && (
            <div className="oc-table-wrap">
              <table className="oc-table">
                <thead>
                  <tr>
                    <th scope="col">Owners</th>
                    <th scope="col">Property</th>
                    <th scope="col">Phase</th>
                    <th scope="col">Signature</th>
                    <th scope="col">Age</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {engagements.map((e) => (
                    <Fragment key={e.caseId}>
                      <tr
                        data-clickable="true"
                        onClick={() =>
                          // WEL-167: before the agreement exists (owner still on the
                          // intake form) the item to open is the INTAKE — the
                          // agreement item does not exist yet and 404s.
                          navigate(
                            `/items/${encodeURIComponent(
                              `${e.signature ? 'property_management_agreement' : 'owner_intake'}:${e.caseId}`,
                            )}`,
                          )
                        }
                      >
                        <td>{e.ownerNames}</td>
                        <td>{e.propertyAddress ?? '—'}</td>
                        <td>
                          <PhaseChip phase={e.phase} sub={e.nativeStateLabel} />
                        </td>
                        <td>
                          {e.signature && e.signature.total > 0
                            ? `${e.signature.signed}/${e.signature.total}`
                            : '—'}
                        </td>
                        <td className="oc-cell-num">{formatAge(e.ageDays)}</td>
                        {/* Resend the pre-agreement intake invite (only while the owner
                            still owes the form) + the Details expander (W-9 requests,
                            AppFolio sync). Stop row-click navigation. */}
                        <td onClick={(ev) => ev.stopPropagation()}>
                          <span style={{ display: 'inline-flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                            {e.canResendIntake && (
                              <button
                                type="button"
                                className="btn"
                                disabled={resendingId === e.caseId}
                                onClick={() => handleResend(e.caseId)}
                              >
                                {resendingId === e.caseId
                                  ? 'Sending…'
                                  : resentIds.has(e.caseId)
                                    ? 'Resend again'
                                    : 'Resend intake'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn"
                              data-variant="ghost"
                              data-size="sm"
                              aria-expanded={openCaseId === e.caseId}
                              onClick={() =>
                                setOpenCaseId((cur) => (cur === e.caseId ? null : e.caseId))
                              }
                            >
                              {openCaseId === e.caseId ? 'Hide details' : 'Details'}
                            </button>
                          </span>
                        </td>
                      </tr>
                      {openCaseId === e.caseId && (
                        <tr>
                          <td colSpan={6}>
                            <EngagementDetails caseId={e.caseId} propertyAddress={e.propertyAddress ?? null} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="oc-meta" style={{ margin: 0 }}>
            Owner agreements ride the same signing spine — owners in parallel, then Agent, then
            Broker.
          </p>
        </>
      )}

      {tab === 'properties' && (
        <>
          {propsLoading && <OcLoading />}
          {!propsLoading && propsError && <OcError>{propsError}</OcError>}
          {!propsLoading && !propsError && properties !== null && (
            <>
              {filteredProperties.length === 0 ? (
                <OcEmpty>No properties match this filter.</OcEmpty>
              ) : (
                <div className="oc-table-wrap">
                  <table className="oc-table">
                    <thead>
                      <tr>
                        <th scope="col">Name</th>
                        <th scope="col">Address</th>
                        <th scope="col">Type</th>
                        <th scope="col">Reference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProperties.map((p) => (
                        <tr key={p.propertyId}>
                          <td>{p.name}</td>
                          <td>{p.address ?? '—'}</td>
                          <td style={{ color: 'var(--fg-muted)' }}>{p.propertyType ?? '—'}</td>
                          <td>
                            <Link to="/hoas">HOA</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="oc-meta" style={{ margin: 0 }}>
                {filteredProperties.length} of {properties.length} properties
              </p>
            </>
          )}
          <div className="oc-panel">
            <span className="oc-kicker">Reference surfaces</span>
            <p className="oc-meta" style={{ margin: 0 }}>
              This branch's other registries: <Link to="/hoas">HOA registry</Link> ·{' '}
              <Link to="/pm-directory">PM directory</Link>.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
